/*
 *  -----------------------------------------------------------------------
 *    <copyright file="add-member-dialog/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';
import cn from 'clsx';

import CopyButton from '@components/core/copy-button';
import { useBoolean, useLocalState, useTranslation } from 'hooks';
import { StudyVisitScheduleFields, StudyTreatmentArmFields } from 'queries/generated/fragments.generated';
import { GET_STUDY_TREATMENT_ARMS, SAVE_STUDY_TREATMENT_ARM } from 'queries/study-treatment-arms';
import { SAVE_STUDY_VISIT_SCHEDULE } from 'queries/study-visit-schedules';
import { checkConfigIdErrors, checkIfNameExist, checkRequiredFields, validateFields } from 'utils/validation';
import Button from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import TextField from 'views/components/core/text-field';

import './styles.scss';

interface AddTreatmentDialogInterface {
    open: boolean;
    onClose: (any: any) => any;
    treatments: StudyTreatmentArmFields[];
    isSysAdmin: boolean;
    currentTreatment?: StudyTreatmentArmFields | null;
    currentSchedule?: StudyVisitScheduleFields | null;
}

const AddTreatmentDialog = ({
    open,
    onClose,
    treatments,
    currentTreatment = null,
    currentSchedule = null,
    isSysAdmin,
}: AddTreatmentDialogInterface) => {
    const { state: localState } = useLocalState();
    const study = localState.selectedStudy;
    const studyId = study?.id;
    const { t } = useTranslation();
    const initialState = {
        id: null,
        configId: null,
        name: '',
        subjectCode: '',
        description: '',
        active: true,
    };
    const initialError = { name: '', subjectCode: '', description: '', configId: '' };

    const [state, setState] = useState({ ...initialState });
    const [errors, setErrors] = useState({ ...initialError } as any);
    const [saveTreatment] = useMutation(SAVE_STUDY_TREATMENT_ARM);
    const [updateSchedule] = useMutation(SAVE_STUDY_VISIT_SCHEDULE);
    const disabledSave = useBoolean(true);

    useEffect(() => {
        if (currentTreatment) {
            setState({
                id: currentTreatment.id,
                configId: currentTreatment.configId,
                name: currentTreatment.name ?? '',
                subjectCode: currentTreatment.subjectCode ?? '',
                description: currentTreatment.description ?? '',
                active: currentTreatment.active ?? true,
            });
        }
    }, [currentTreatment]);

    const close = (action: string | boolean | undefined) => {
        if (action !== 'error') {
            setState({ ...initialState });
            setErrors({ ...initialError });
        }
        disabledSave.setTrue();
        onClose(action);
    };

    const validate = (value?: string, field?: string, length?: number) => {
        let error = validateFields(value, field, length);

        const configIdError = checkConfigIdErrors(state, treatments, value);
        if (field === 'configId') {
            error = { configId: configIdError };
        }

        setErrors({
            ...errors,
            ...error,
        });
    };

    const onChange = (name: string) => (event: any) => {
        const { value } = event.target;
        let length = 30;
        if (name === 'name') length = 60;
        if (name === 'description') length = 150;
        if (name === 'configId') length = 24;

        disabledSave.setFalse();

        setState({
            ...state,
            [name]: value,
        });
        validate(value, name, length);
    };

    const onCheckboxChange = (fieldName: 'active') => () => {
        disabledSave.setFalse();
        setState({ ...state, [fieldName]: !state[fieldName] });
    };

    const getButtonLabel = () => {
        if (currentSchedule && !currentTreatment) return 'common:createAssign';
        return currentTreatment ? 'update' : 'add';
    };

    const submit = () => {
        const requiredFields = ['subjectCode', 'name'];
        disabledSave.setTrue();

        if (currentTreatment) {
            requiredFields.push('configId');
            const configIdError = checkConfigIdErrors(state, treatments);

            if (configIdError) {
                setErrors({
                    ...errors,
                    configId: configIdError,
                });
                return;
            }
        }

        if (checkRequiredFields({ fields: requiredFields, state, setErrors, errors })) {
            const isSubjectCodeExist = checkIfNameExist(
                { ...state, subjectCode: state.subjectCode.trim(), id: null },
                treatments,
                'subjectCode',
            );
            const isNameExist = checkIfNameExist({ ...state, name: state.name.trim(), id: null }, treatments, 'name');
            const checkCurrentSubjectCode =
                currentTreatment && currentTreatment.subjectCode !== state.subjectCode && isSubjectCodeExist;
            const checkCurrentCrfName = currentTreatment && currentTreatment.name !== state.name && isNameExist;

            if (
                ((isSubjectCodeExist || isNameExist) && currentTreatment === null) ||
                checkCurrentSubjectCode ||
                checkCurrentCrfName
            ) {
                disabledSave.setTrue();
                if (checkCurrentSubjectCode || isSubjectCodeExist)
                    setErrors({ ...errors, subjectCode: t('admin:treatmentSubjectCodeAlreadyExists') });

                if (checkCurrentCrfName || isNameExist)
                    setErrors({ ...errors, name: t('admin:treatmentNameAlreadyExists') });

                return;
            }

            const submitData = {
                organisationKey: study?.organisationKey,
                studyId,
                treatmentArm: {
                    id: currentTreatment?.id || null,
                    configId: state.configId || null,
                    subjectCode: state.subjectCode,
                    name: state.name,
                    description: state.description,
                    active: state.active,
                },
            };

            if (currentSchedule) {
                saveTreatment({
                    variables: {
                        ...submitData,
                    },
                })
                    .then((res: any) => {
                        const ids = currentSchedule.treatmentArmIds.concat(res.data.upsertTreatmentArm.id);
                        updateSchedule({
                            variables: {
                                studyId,
                                visitSchedule: {
                                    id: currentSchedule?.id ?? null,
                                    name: currentSchedule.name,
                                    description: currentSchedule.description,
                                    active: currentSchedule.active,
                                    treatmentArmIds: ids,
                                    cohortIds: currentSchedule?.cohortIds ?? [],
                                },
                                reason: '',
                            },
                            refetchQueries: [
                                {
                                    query: GET_STUDY_TREATMENT_ARMS,
                                    variables: { studyId },
                                },
                            ],
                        });
                    })
                    .then(() => {
                        close('add');
                    })
                    .catch(() => {
                        close('error');
                    });
            } else {
                saveTreatment({
                    variables: {
                        ...submitData,
                    },
                    refetchQueries: [
                        {
                            query: GET_STUDY_TREATMENT_ARMS,
                            variables: { studyId },
                        },
                    ],
                })
                    .then(() => {
                        close(currentTreatment ? 'edit' : 'success');
                    })
                    .catch(() => {
                        close('error');
                    });
            }
        }
    };

    if (!open) return null;

    return (
        <CustomDialog
            onClose={close}
            fullWidth
            maxWidth="md"
            scroll="paper"
            open={open}
            className="custom-width-popup max-w700"
        >
            <DialogTitle id="add-treatment-arms-modal-title">
                {currentTreatment
                    ? t('admin:editTreatmentWithId', { id: currentTreatment.recordId })
                    : t('admin:addTreatment')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        close(false);
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="add-treatment-dialog-content">
                <div className="checkboxes-wrapper">
                    <div className="left-part mb-15">
                        <Checkbox
                            rounded={null}
                            id="treatment-active-checkbox"
                            className="cell-checkbox no-border mr"
                            checked={state.active}
                            onChange={onCheckboxChange('active')}
                            label={t<string>('common:active')}
                            disabled={isSysAdmin}
                            data-testid="treatment-active-checkbox"
                            size="medium"
                        />
                    </div>
                </div>

                <div className="type-edc-wrapper mb">
                    <FormControl variant="outlined" className="form-control-type mr mb">
                        <TextField
                            label={t('admin:sourceSystemId')}
                            id="edit-treatment-subject-code"
                            fullWidth
                            required
                            value={state.subjectCode}
                            onChange={onChange('subjectCode')}
                            placeholder={t('admin:enterSourceSystemId')}
                            helperText={
                                <CharactersRemaining
                                    id="create-treatment-dialog-subject-code"
                                    length={state.subjectCode.length}
                                    max={30}
                                    error={errors.subjectCode}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                            error={!!errors.subjectCode}
                            disabled={isSysAdmin}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type mb">
                        <TextField
                            label={t('name')}
                            id="edit-treatment-name"
                            fullWidth
                            required
                            value={state.name}
                            onChange={onChange('name')}
                            placeholder={t('admin:enterName')}
                            helperText={
                                <CharactersRemaining
                                    id="create-treatment-dialog-name"
                                    length={state.name?.length}
                                    max={60}
                                    error={errors.name}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                            error={!!errors.name}
                            disabled={isSysAdmin}
                        />
                    </FormControl>
                </div>
                <div className={cn('type-edc-wrapper mb', { 'mb-10': currentTreatment })}>
                    <TextField
                        label={`${t('description')}`}
                        id="edit-treatment-description"
                        multiline
                        rows={5}
                        fullWidth
                        value={state.description}
                        onChange={onChange('description')}
                        placeholder={t('ra:criticalVariableDescriptionHelperText')}
                        helperText={
                            <CharactersRemaining
                                id="create-treatment-dialog-description"
                                length={state.description.length}
                                max={150}
                                error={errors.description}
                            />
                        }
                        variant="outlined"
                        color="secondary"
                        error={!!errors.description}
                        className="before-select"
                        disabled={isSysAdmin}
                    />
                </div>
                {!!currentTreatment && (
                    <div className="type-edc-wrapper">
                        <FormControl variant="outlined" className="form-control-type">
                            <TextField
                                readOnly={!isSysAdmin}
                                id="edit-field-config-id"
                                label={t('admin:configId')}
                                onChange={onChange('configId')}
                                fullWidth
                                value={state.configId}
                                size="small"
                                InputProps={{
                                    readOnly: !isSysAdmin,
                                    endAdornment: <CopyButton value={state.configId ?? ''} />,
                                }}
                                helperText={
                                    <span style={{ color: '#ae0015' }} className="remaining-text">
                                        {errors.configId}
                                    </span>
                                }
                                variant="outlined"
                                color="secondary"
                                error={!!errors.configId}
                            />
                        </FormControl>
                    </div>
                )}
                <DialogActions className={`add-treatment-dialog-content-actions ${!currentTreatment && 'flex-end'}`}>
                    <ModalUpdatedInfo item={currentTreatment} />
                    <Button
                        id="submit-treatment-btn"
                        className="add-treatment-btn"
                        disabled={Object.keys(errors).filter((item) => errors[item]).length > 0 || disabledSave.value}
                        text={getButtonLabel()}
                        onClick={submit}
                    />
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default AddTreatmentDialog;
